import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useAuthStore } from "@/stores/authStore";

interface OnboardingData {
  // Page 1: Script Upload
  scriptFile: File | null;
  scriptFileName: string | null;
  projectId?: string;
  scriptId?: string;

  // Page 2: Project Details
  projectTitle: string;
  projectType: string;
  episodeCount: number;
  budget: string;
  logline: string;

  // Page 3: Socials
  instagram: string;
  tiktok: string;
  spotify: string;
  applePodcast: string;
  isEarly: boolean;

  // Page 3: Film/TV (New)
  imdbProfile: string;
  projectRole: string[];
  collaborators: { name: string; role: string[]; imdbProfile: string }[];

  // Page 4: Audience Location
  audienceLocations: string[];

  // Page 5: IMDb
  imdbUrl: string;

  // New Project Flow
  sameAsPrior: boolean;
}

interface OnboardingContextType {
  data: OnboardingData;
  updateData: (newData: Partial<OnboardingData>) => void;
  resetData: () => void;
}

const getDefaultData = (): OnboardingData => ({
  scriptFile: null,
  scriptFileName: null,
  projectId: undefined,
  scriptId: undefined,
  projectTitle: "",
  projectType: "",
  episodeCount: 1,
  budget: "",
  logline: "",
  instagram: "",
  tiktok: "",
  spotify: "",
  applePodcast: "",
  isEarly: false,
  imdbProfile: "",
  projectRole: [],
  collaborators: [],
  audienceLocations: [],
  imdbUrl: "",
  sameAsPrior: false,
});

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuthStore();
  
  const [data, setData] = useState<OnboardingData>(() => {
    // Try to load from local storage on initial load
    const savedData = localStorage.getItem('onboarding_v2_data');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Note: File objects cannot be stored in localStorage, so scriptFile will be null
        // We persist scriptFileName to show in UI
        return { ...getDefaultData(), ...parsed };
      } catch (e) {
        console.error("Failed to parse onboarding data", e);
      }
    }
    return getDefaultData();
  });

  useEffect(() => {
    // Persist to local storage whenever data changes
    // We exclude scriptFile because File objects aren't serializable
    const { scriptFile, ...serializableData } = data;
    localStorage.setItem('onboarding_v2_data', JSON.stringify(serializableData));
  }, [data]);

  // Reset data when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setData(getDefaultData());
      localStorage.removeItem('onboarding_v2_data');
    }
  }, [isAuthenticated]);

  const updateData = useCallback((newData: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const resetData = useCallback(() => {
    console.log("OnboardingContext: resetData called");
    setData(getDefaultData());
    localStorage.removeItem('onboarding_v2_data');
  }, []);

  return (
    <OnboardingContext.Provider value={{ data, updateData, resetData }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};